(() => {
  const money = (cents, currency = 'INR') => {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: window.Shopify?.currency?.active || currency,
        maximumFractionDigits: 0,
      }).format(Number(cents) / 100);
    } catch {
      return `₹${(Number(cents) / 100).toFixed(0)}`;
    }
  };

  function initPlaceholderRotation(root, input) {
    const raw = root.dataset.placeholders || '';
    const titles = raw
      .split('||')
      .map((t) => t.trim())
      .filter(Boolean);
    if (!titles.length) return;

    let index = 0;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const apply = () => {
      if (document.activeElement === input || input.value) return;
      input.setAttribute('placeholder', titles[index]);
      index = (index + 1) % titles.length;
    };
    apply();
    if (!reduce) setInterval(apply, 2600);
  }

  function initPredictiveSearch(root) {
    const form = root.querySelector('[data-search-form]');
    const input = root.querySelector('[data-search-input]');
    const results = root.querySelector('[data-search-results]');
    const clearBtn = root.querySelector('[data-search-clear]');
    if (!form || !input || !results) return;

    initPlaceholderRotation(root, input);

    let timer = null;
    let controller = null;

    const closeResults = () => {
      results.hidden = true;
      results.innerHTML = '';
      input.setAttribute('aria-expanded', 'false');
    };

    const openResults = () => {
      results.hidden = false;
      input.setAttribute('aria-expanded', 'true');
    };

    const formatPrice = (product) => {
      if (product.priceLabel) return product.priceLabel;
      if (product.price != null && product.price !== '') return money(product.price);
      return '';
    };

    const render = (products, query) => {
      if (!products.length) {
        results.innerHTML = `<div class="header-search__empty">No products found for “${query}”</div>`;
        openResults();
        return;
      }
      results.innerHTML = products
        .map((product) => {
          const img = product.image || '';
          const price = formatPrice(product);
          return `
            <a class="header-search__result" role="option" href="${product.url}">
              <span class="header-search__result-media">
                ${img ? `<img src="${img}" alt="" width="48" height="48" loading="lazy">` : ''}
              </span>
              <span class="header-search__result-meta">
                <strong>${product.title}</strong>
                ${price ? `<span>${price}</span>` : ''}
              </span>
            </a>
          `;
        })
        .join('');
      openResults();
    };

    const search = async (query) => {
      if (!query) {
        closeResults();
        return;
      }
      if (controller) controller.abort();
      controller = new AbortController();

      try {
        const url = `${root.dataset.suggestUrl || '/search/suggest.json'}?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=8&resources[options][unavailable_products]=last`;
        const res = await fetch(url, {
          headers: { Accept: 'application/json' },
          signal: controller.signal,
        });
        if (!res.ok) throw new Error('suggest failed');
        const data = await res.json();
        const normalized = (data.resources?.results?.products || []).map((p) => {
          let priceCents = null;
          let priceLabel = '';
          if (typeof p.price === 'number') priceCents = p.price;
          else if (typeof p.price === 'string' && /[₹$€£]/.test(p.price)) priceLabel = p.price;
          else if (typeof p.price === 'string' && p.price.trim() !== '') {
            const n = Number(p.price.replace(/[^\d.]/g, ''));
            if (!Number.isNaN(n)) priceCents = n < 1000 && !p.price.includes('.') ? n : n * 100;
            // Predictive search often returns decimal rupees as "999.00"
            if (!Number.isNaN(n) && p.price.includes('.')) priceCents = Math.round(n * 100);
          }
          return {
            title: p.title,
            url: p.url,
            image: p.image || p.featured_image || '',
            price: priceCents,
            priceLabel,
          };
        });
        render(normalized, query);
      } catch (err) {
        if (err.name === 'AbortError') return;
        try {
          const res = await fetch('/products.json?limit=250');
          const data = await res.json();
          const q = query.toLowerCase();
          const matched = (data.products || [])
            .filter((p) => p.title.toLowerCase().includes(q) || (p.handle || '').includes(q))
            .slice(0, 8)
            .map((p) => ({
              title: p.title,
              url: `/products/${p.handle}`,
              image: p.images?.[0]?.src || '',
              price: p.variants?.[0] ? Math.round(Number(p.variants[0].price) * 100) : null,
            }));
          render(matched, query);
        } catch {
          closeResults();
        }
      }
    };

    input.addEventListener('input', () => {
      const value = input.value.trim();
      clearBtn.hidden = !value;
      window.clearTimeout(timer);
      timer = window.setTimeout(() => search(value), 120);
    });

    input.addEventListener('focus', () => {
      if (input.value.trim()) search(input.value.trim());
    });

    clearBtn?.addEventListener('click', () => {
      input.value = '';
      clearBtn.hidden = true;
      closeResults();
      input.focus();
    });

    document.addEventListener('click', (e) => {
      if (!root.contains(e.target)) closeResults();
    });

    form.addEventListener('submit', (e) => {
      if (!input.value.trim()) e.preventDefault();
    });
  }

  function syncHeaderOffset() {
    const isMobile = window.matchMedia('(max-width: 899px)').matches;
    const chrome = document.querySelector('[data-site-chrome]');
    const header = document.querySelector('.site-header');
    const quickNav = document.querySelector('[data-mobile-quick-nav]');

    // Only write a variable when the value really changed (avoids needless style recalcs / shifts).
    const setVar = (name, value) => {
      const root = document.documentElement;
      if (root.style.getPropertyValue(name) !== value) root.style.setProperty(name, value);
    };

    if (header) {
      const headerH = Math.ceil(header.offsetHeight || 48);
      setVar('--site-header-sticky-top', `${headerH}px`);
      setVar('--header-height', `${headerH}px`);
    }

    if (quickNav) {
      const qh = Math.ceil(quickNav.offsetHeight || 64);
      setVar('--mobile-quick-nav-height', `${qh}px`);
    }

    if (!chrome) return;

    let spacer = document.querySelector('[data-header-group-spacer]');
    if (isMobile) {
      if (!spacer) {
        spacer = document.createElement('div');
        spacer.setAttribute('data-header-group-spacer', '');
        spacer.setAttribute('aria-hidden', 'true');
        const section = chrome.closest('.shopify-section') || chrome;
        section.insertAdjacentElement('afterend', spacer);
      }
      const height = Math.ceil(chrome.getBoundingClientRect().height || chrome.offsetHeight);
      if (height > 0) {
        // Sticky elements follow the real chrome height; the spacer keeps the full height while the announcement
        // bar is collapsed on scroll, so page content never jumps up under the reader.
        setVar('--header-group-offset', `${height}px`);
        const current = parseFloat(spacer.style.height) || 0;
        const collapsed = chrome.classList.contains('is-announcement-hidden');
        if ((!collapsed || height > current) && Math.abs(height - current) >= 1) spacer.style.height = `${height}px`;
      }
    } else if (spacer) {
      spacer.remove();
      document.documentElement.style.removeProperty('--header-group-offset');
      chrome.classList.remove('is-announcement-hidden');
    }
  }

  function initMobileBrandSearch() {
    const chrome = document.querySelector('[data-site-chrome]');
    const searchWrap = document.querySelector('[data-header-search]');
    if (!chrome || !searchWrap) return;

    let syncTimer = 0;
    const scheduleOffsetSync = () => {
      window.cancelAnimationFrame(syncTimer);
      syncTimer = window.requestAnimationFrame(() => {
        syncHeaderOffset();
        window.requestAnimationFrame(() => {
          syncHeaderOffset();
          document.documentElement.dispatchEvent(new CustomEvent('mukhlis:chrome-offset'));
        });
      });
      window.clearTimeout(scheduleOffsetSync._t);
      scheduleOffsetSync._t = window.setTimeout(() => {
        syncHeaderOffset();
        document.documentElement.dispatchEvent(new CustomEvent('mukhlis:chrome-offset'));
      }, 340);
    };

    const onScroll = () => {
      const y = window.scrollY || window.pageYOffset || 0;
      const isMobile = window.matchMedia('(max-width: 899px)').matches;
      chrome.classList.toggle('is-scrolled', y > 8);

      if (isMobile) {
        const hideAnnouncement = y > 18;
        const wasHidden = chrome.classList.contains('is-announcement-hidden');
        chrome.classList.toggle('is-announcement-hidden', hideAnnouncement);
        searchWrap.classList.toggle('is-search-mode', y > 24);
        chrome.classList.toggle('has-mobile-search', y > 24);
        if (wasHidden !== hideAnnouncement) scheduleOffsetSync();
      } else {
        chrome.classList.remove('is-announcement-hidden', 'has-mobile-search');
        searchWrap.classList.add('is-search-mode');
      }
    };

    syncHeaderOffset();
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', () => {
      syncHeaderOffset();
      onScroll();
    });

    const announcement = chrome.querySelector('.announcement-bar');
    announcement?.addEventListener('transitionend', (e) => {
      if (e.propertyName === 'max-height' || e.propertyName === 'opacity') {
        syncHeaderOffset();
      }
    });
  }

  function initMobileNav() {
    const toggle = document.querySelector('[data-nav-toggle]');
    const panel = document.querySelector('[data-nav-panel]');
    const overlay = document.querySelector('[data-nav-overlay]');
    const closeBtn = document.querySelector('[data-nav-close]');
    if (!toggle || !panel) return;

    // Portal to body so fixed overlay/drawer cover the full viewport
    // (fixed header group creates a containing block otherwise).
    if (overlay && overlay.parentElement !== document.body) {
      document.body.appendChild(overlay);
    }
    if (panel.parentElement !== document.body) {
      document.body.appendChild(panel);
    }

    const openIcon = toggle.querySelector('[data-nav-icon-open]');
    const closeIcon = toggle.querySelector('[data-nav-icon-close]');
    let lastFocus = null;

    const setOpen = (open) => {
      panel.classList.toggle('is-open', open);
      overlay?.classList.toggle('is-open', open);
      toggle.setAttribute('aria-expanded', String(open));
      toggle.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
      panel.setAttribute('aria-hidden', String(!open));
      overlay?.setAttribute('aria-hidden', String(!open));
      document.body.classList.toggle('nav-open', open);
      if (openIcon) openIcon.hidden = open;
      if (closeIcon) closeIcon.hidden = !open;

      if (open) {
        lastFocus = document.activeElement;
        const focusTarget = closeBtn || panel.querySelector('a, button');
        window.setTimeout(() => focusTarget?.focus?.(), 40);
      } else if (lastFocus && typeof lastFocus.focus === 'function') {
        lastFocus.focus();
      }
    };

    toggle.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      setOpen(!panel.classList.contains('is-open'));
    });

    closeBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      setOpen(false);
    });

    overlay?.addEventListener('click', () => setOpen(false));

    panel.querySelectorAll('a').forEach((link) => {
      link.addEventListener('click', () => setOpen(false));
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && panel.classList.contains('is-open')) {
        setOpen(false);
      }
    });
  }

  function initMobileQuickNav() {
    const nav = document.querySelector('[data-mobile-quick-nav]');
    if (!nav) return;

    const buttons = [...nav.querySelectorAll('[data-quick-key]')];

    buttons.forEach((btn) => {
      btn.addEventListener('click', () => {
        const key = btn.dataset.quickKey;
        // Instant highlight so users see selection before navigation / WhatsApp opens
        buttons.forEach((b) => {
          const isTarget = b === btn;
          if (key === 'whatsapp') {
            b.classList.toggle('is-pressed', isTarget);
          } else {
            b.classList.toggle('is-active', isTarget);
            b.classList.remove('is-pressed');
          }
        });
        window.requestAnimationFrame(syncHeaderOffset);
      });
    });

    window.setTimeout(syncHeaderOffset, 100);
    window.setTimeout(syncHeaderOffset, 500);
    window.addEventListener('load', syncHeaderOffset);
  }

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('[data-header-search]').forEach(initPredictiveSearch);
    initMobileBrandSearch();
    initMobileNav();
    initMobileQuickNav();
  });
})();
